import { useMemo, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { SensitiveText, usePrivacyMasking } from "@/components/common/Sensitive";
import { useKaiSlashCommands } from "@/hooks/useKaiSlashCommands";
import { parseMessageAttachments } from "@/lib/kaiAttachments";
import type { KaiMessage } from "./kaiTypes";
import {
  entityPillClassName,
  stripTaggedContentSuffix,
  type KaiEntityTag,
} from "./kaiEntityTags";
import { KaiEntityTagInlineBody } from "./KaiEntityTagVisual";
import {
  inferSlashCommandFromContent,
  slashPillClassName,
} from "./kaiSlashCommands";
import { KaiMarkdown } from "./kaiMarkdown";
import { KaiMessageActions } from "./KaiMessageActions";
import { KaiMessageAttachments } from "./KaiMessageAttachments";
import { useKaiChat } from "./KaiChatContext";
import { useKaiMarkdownActions } from "./useKaiMarkdownActions";

/**
 * Render a user message with tagged entities shown as inline pills, positioned
 * exactly where the user placed them (e.g. "show bookings for @Acme"). Each
 * tag's inline `@label` occurrence is wrapped; party names stay privacy-masked.
 */
function renderTaggedText(text: string, tags: KaiEntityTag[]): ReactNode {
  if (tags.length === 0) return text;
  const matches: { index: number; length: number; tag: KaiEntityTag }[] = [];
  for (const tag of tags) {
    const withAt = `@${tag.label}`;
    let index = text.indexOf(withAt);
    let length = withAt.length;
    if (index === -1) {
      index = text.indexOf(tag.label);
      length = tag.label.length;
    }
    if (index === -1) continue;
    matches.push({ index, length, tag });
  }
  if (matches.length === 0) return text;
  matches.sort((a, b) => a.index - b.index);

  const nodes: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.index < cursor) return; // skip overlapping matches
    if (m.index > cursor) nodes.push(text.slice(cursor, m.index));
    const useInlineBody =
      m.tag.kind === "carrier" || m.tag.kind === "contract_ref";
    const sensitive =
      m.tag.kind !== "user" && m.tag.kind !== "port" && !useInlineBody;
    nodes.push(
      <span
        key={`tag-${i}`}
        className={cn(
          "mx-px inline-flex items-center gap-0.5 whitespace-nowrap rounded-md px-1 py-px font-medium",
          entityPillClassName(m.tag.kind),
        )}
      >
        {useInlineBody ? (
          <KaiEntityTagInlineBody tag={m.tag} />
        ) : sensitive ? (
          <>@{<SensitiveText value={m.tag.label} />}</>
        ) : (
          `@${m.tag.label}`
        )}
      </span>,
    );
    cursor = m.index + m.length;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/**
 * Render a user message that started with a `/` slash command as a leading
 * command pill followed by the remaining text (with entity tags still inlined).
 * Falls back to plain tagged text when the marker is absent.
 */
function renderUserText(
  text: string,
  tags: KaiEntityTag[],
  slash?: { id: string; label: string },
): ReactNode {
  if (!slash) return renderTaggedText(text, tags);
  const marker = `/${slash.label}`;
  if (!text.startsWith(marker)) return renderTaggedText(text, tags);
  const rest = text.slice(marker.length);
  return (
    <>
      <span
        className={cn(
          "mr-px whitespace-nowrap rounded-md px-1 py-px font-medium",
          slashPillClassName(),
        )}
      >
        {marker}
      </span>
      {renderTaggedText(rest, tags)}
    </>
  );
}

interface KaiMessageBubbleProps {
  message: KaiMessage;
  isStreaming?: boolean;
  artifacts?: Record<string, unknown>;
  /** Conversation the message belongs to — needed to fetch image thumbnails. */
  conversationId?: string | null;
}

export function KaiMessageBubble({
  message,
  isStreaming,
  artifacts,
  conversationId,
}: KaiMessageBubbleProps) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const mask = usePrivacyMasking();
  const actions = useKaiMarkdownActions();
  const kai = useKaiChat();
  const slashCommands = useKaiSlashCommands();
  const entityTags = message.entityTags ?? [];
  const userText = isUser
    ? stripTaggedContentSuffix(message.content)
    : message.content;
  const slashCommand = useMemo(() => {
    if (!isUser) return undefined;
    if (message.slashCommand) return message.slashCommand;
    return inferSlashCommandFromContent(userText, slashCommands);
  }, [isUser, message.slashCommand, slashCommands, userText]);
  const attachments = isUser ? parseMessageAttachments(message.metadata) : [];
  const copyText = isUser ? userText : message.content;
  const showActions =
    !isStreaming &&
    copyText.trim().length > 0 &&
    (isUser || isAssistant);
  const feedbackSentiment = kai?.messageFeedback[message.id];
  const onFeedback =
    isAssistant && kai && conversationId
      ? (sentiment: "helpful" | "not_helpful") =>
          kai.submitMessageFeedback(message.id, sentiment)
      : undefined;

  return (
    <div
      className={cn(
        "group/msg flex w-full flex-col gap-0.5",
        isUser ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "min-w-0 max-w-[min(100%,38rem)] rounded-2xl px-4 py-3 text-sm",
          isUser
            ? "glass-panel-strong clay-strong bg-primary/10 text-foreground"
            : "glass-panel text-foreground/90",
        )}
      >
        {isUser ? (
          <>
            {attachments.length > 0 && conversationId && (
              <KaiMessageAttachments
                conversationId={conversationId}
                attachments={attachments}
              />
            )}
            {userText && (
              <p className="whitespace-pre-wrap text-left leading-relaxed">
                {renderUserText(userText, entityTags, slashCommand)}
              </p>
            )}
          </>
        ) : (
          <KaiMarkdown
            text={message.content}
            ctx={{
              isStreaming,
              artifacts,
              maskSensitive: mask,
              conversationId,
              onSendFollowUp: actions.onSendFollowUp,
              beforeNavigate: actions.beforeNavigate,
              onReviewEmail: actions.onReviewEmail,
            }}
          />
        )}
      </div>
      {showActions && (
        <KaiMessageActions
          content={copyText}
          sentiment={feedbackSentiment}
          onFeedback={onFeedback}
          showFeedback={isAssistant}
          className={cn(
            "px-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover/msg:opacity-100 sm:focus-within:opacity-100",
            feedbackSentiment != null && "sm:opacity-100",
            isUser && "justify-end",
          )}
        />
      )}
    </div>
  );
}

interface KaiWorkingBubbleProps {
  message: string;
}

/** Thinking / finalizing bubble while Kai is generating with no prose yet. */
export function KaiWorkingBubble({ message }: KaiWorkingBubbleProps) {
  return (
    <div className="flex justify-start">
      <div className="glass-panel flex items-center gap-2 rounded-2xl px-4 py-3 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:0ms]" />
          <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:150ms]" />
          <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:300ms]" />
        </span>
        <span>{message}</span>
      </div>
    </div>
  );
}

interface KaiTypingIndicatorProps {
  className?: string;
}

export function KaiTypingIndicator({ className }: KaiTypingIndicatorProps) {
  return (
    <div className={cn("flex justify-start", className)}>
      <div className="glass-panel flex items-center gap-1.5 rounded-2xl px-4 py-3">
        <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:0ms]" />
        <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:150ms]" />
        <span className="size-1.5 animate-pulse rounded-full bg-primary [animation-delay:300ms]" />
      </div>
    </div>
  );
}
